import React, { useEffect, useState } from 'react';

interface PressureFrameProps {
  pressureLevel: 0 | 1 | 2 | 3 | 4;
  // Drives the clamp re-check every poll tick (5s, see useTabletopFeed),
  // independent of whether pressureLevel's own value changed since the last
  // tick — without this, a target reached over several ticks would never
  // re-evaluate once pressureLevel stopped changing mid-climb.
  simTimeMs: number;
  children: React.ReactNode;
}

// Checkpoint 1: only the data-pressure attribute and the clamp exist. The
// colour/glow/vignette treatment per level (checkpoint 2) is declared in
// index.css under [data-pressure="0..4"], following the
// [data-participant-theme] custom-property pattern — this component only
// ever needs the attribute, never the values themselves.
const PressureFrame: React.FC<PressureFrameProps> = ({ pressureLevel, simTimeMs, children }) => {
  const [displayedLevel, setDisplayedLevel] = useState<0 | 1 | 2 | 3 | 4>(pressureLevel);

  useEffect(() => {
    setDisplayedLevel((current) => {
      if (pressureLevel > current) return (current + 1) as 0 | 1 | 2 | 3 | 4;
      if (pressureLevel < current) return (current - 1) as 0 | 1 | 2 | 3 | 4;
      return current;
    });
    // Re-runs every sim-time tick (poll), not just when pressureLevel itself
    // changes — see the comment on the prop above. Plan 04 already clamps
    // server-side; this is belt-and-braces for the fallback path (no
    // adjudicator running), which has no such guard of its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pressureLevel, simTimeMs]);

  return (
    <div
      data-pressure={displayedLevel}
      className="relative w-full h-full bg-[var(--pressure-bg,#052831)] transition-colors duration-1000 overflow-hidden"
    >
      {children}
    </div>
  );
};

export default PressureFrame;
